<template>
  <ProductCard
    :title="title"
    :icon="{
      value: {
        src: productIcon,
        height: '40',
        width: '40',
        alt: 'Product card',
      },
    }"
    :subTitle="subTitle"
    :description="description"
    :renewalText="renewalText"
    :noDescriptionText="rsaStatus?.noVehicleInRsa"
    :alignTitleCenter="rsaStatus?.noVehicleInRsa && warningMessage != ''"
    :productLinkUrl="!rsaStatus?.isLifeStyle && !isBank ? linkUrl : undefined"
    :dataTestId="dataTestId"
  >
    <template  #topContent>
      <div v-if="isOnExcessUseProgram" class="rounded-md m-4 mb-0 px-4 py-3 bg-racq-info-blue">
        <RichText data-test-id="excessUseProgramText" class="text-sm" :field="fields?.excessUseProgramText" />
      </div>
      <BannnerList :banners="policyBanners" />
    </template>
    
    <template v-if="rsaStatus?.isLifeStyle || isBank" #bottomInnerContent>
      <div class="pl-0 md:pl-16 pt-4">
        <div class="flex flex-row flex-wrap gap-4">
          <button data-test-id="primaryBtnText" :onclick="primaryBtnLinkClick" class="btn btn-secondary btn-sm">
            {{ isBank ? fields?.bankPrimaryButtonText?.value : fields?.btnPrimaryText?.value }}
          </button>
          <button
            data-test-id="secondaryBtnText"
            :onclick="secondaryBtnLinkClick"
            class="btn btn-sm btn-tertiary input-long-arrow-right"
          >
            {{ isBank ? fields?.bankSecondaryButtonText?.value : fields?.btnSecondaryText?.value }}
          </button>
        </div>
      </div>
    </template>
    <template #bottomOuterContent>
      <ProductStatus
        v-if="warningMessage"
        :makeAPaymentLink="fields?.makeAPaymentLink"
        :showPayNowButton="rsaStatus?.showPayNowButton"
        :payButtonText="fields?.payNowBtnText.value"
        :prn="assistanceProduct?.prn"
        :productStatus="
          rsaStatus?.isOverDue || rsaStatus?.pendingPayment ? RacqProductStatus.Cancelled : RacqProductStatus.Scheduled
        "
        :showButton="rsaStatus?.showPayNowButton"
        :message="warningMessage"
        :dataTestId="dataTestId"
      />
    </template>
  </ProductCard>
</template>
<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue';
import { OwnedAssistanceProduct, ProductContentFields, RacqProductStatus } from '@/interfaces';
import ProductCard from '../ProductCard/ProductCard.vue';
import { AssistanceStatus } from '@/constants/portfolio.const';
import { replaceBookmark } from '@/utils/bookmark.replace';
import { formatDate } from 'date-fns';
import ProductStatus from '../ProductStatus/ProductStatus.vue';
import { isAndroid, isIOS } from '@/utils/device-helper';
import { getRsaPolicyStatusDetails, IRSAPolicyStatusRules } from '@/utils/self-service-rules/RSAPolicyStatusRules';
import { RSA_ROADSIDE_EXCESS_USE } from '@/utils/roadside-helpers';
import { RichText } from '@sitecore-jss/sitecore-jss-vue';
import { LinkField } from '@sitecore-jss/sitecore-jss-vue/types/components/Link';
import { useRoadsideAssistance } from '@/store';
import { IRoadsideContentInterface } from '@/interfaces/self-service/roadside.interface';
import BannnerList from '../Banner/BannnerList.vue';

// Define props and emitters
// ------------------------------------------------------------- //
export interface RoadsideProductCard {
  assistanceProduct?: OwnedAssistanceProduct;
  isBank?: boolean;
  fields?: ProductContentFields;
  dataTestId?: string;
}

const props = defineProps<RoadsideProductCard>();

// Define Store
// ------------------------------------------------------------- //
const roadsideStore = useRoadsideAssistance();

// Define Component state
// ------------------------------------------------------------- //
enum Bookmarks {
  termEndDate = 'termEndDate',
  policyId = 'policyId',
  rsaStatus = 'rsaStatus',
}
const warningMessage = ref<string>('');
const rsaStatus = ref<IRSAPolicyStatusRules>();
const rsaContent = ref<IRoadsideContentInterface>();
// Define computed
// ------------------------------------------------------------- //

const description = computed(() => {
  if (props.isBank) {
    return props.fields?.bankSubtitle?.value;
  } else {
    return rsaStatus.value?.isLifeStyle ? rsaContent.value?.description : '';
  }
});

const subTitle = computed(() => {
  if (rsaStatus.value?.isFordNCO) {
    return rsaContent.value?.subTitle;
  } else {
    return `${
      !props.assistanceProduct?.isLifestyle && props.assistanceProduct?.vehicleDetail
        ? `${props.assistanceProduct?.vehicleDetail?.manufacturingYear} ${props.assistanceProduct?.vehicleDetail?.make?.description} ${props.assistanceProduct?.vehicleDetail?.model?.description} (${props.assistanceProduct.vehicleDetail?.registrationNumber})`
        : ''
    }`;
  }
});

const title = computed(() => {
  if (props.isBank) {
    return props.fields?.bankTitle?.value;
  } else {
    return rsaContent.value?.tierDescription && rsaContent.value?.tierDescription != ''
      ? rsaContent.value?.tierDescription
      : rsaContent.value?.productName;
  }
});

const productIcon = computed(() => {
  if (props.isBank) {
    return props.fields?.bankIcon?.value?.src;
  }
  if (rsaStatus.value?.isLifeStyle) {
    return props.fields?.lifestyleIcon?.value?.src;
  }
  return props.fields?.rsaIcon?.value?.src;
});

const policyBanners = computed(() => rsaContent.value?.policyBanners?.map((banner) => ({
  ...banner,
  ctaLink: (banner.ctaLink ?? '').replace('{policyId}', props.assistanceProduct?.policyId ?? '')     
})) ?? []);

const isOnExcessUseProgram = computed(() => props.assistanceProduct?.productCode == RSA_ROADSIDE_EXCESS_USE);

const linkUrl = computed((): LinkField => {
  let queryString = replaceBookmark(
    Bookmarks.policyId,
    props.fields?.rsaPageUrl?.value?.querystring ?? '',
    props.assistanceProduct?.policyId ?? ''
  );
  queryString = replaceBookmark(Bookmarks.rsaStatus, queryString, props.assistanceProduct?.status ?? '');
  return {
    value: {
      href: replaceBookmark(
        Bookmarks.policyId,
        props.fields?.rsaPageUrl?.value?.href ?? '',
        props.assistanceProduct?.policyId ?? ''
      ),
      querystring: queryString,
    },
  };
});

const renewalText = computed(() => {
  if (props.assistanceProduct?.status == AssistanceStatus.Activated) {
    return replaceBookmark(
      Bookmarks.termEndDate,
      rsaStatus.value?.isLifeStyle
        ? (props.fields?.lifeStyleRenewalDueInfoText?.value ?? '')
        : (props.fields?.rsaRenewalDueInfoText?.value ?? ''),
      formatDate(new Date(props.assistanceProduct.endDate), 'dd/MM/yyyy')
    );
  }
  return '';
});

// Lifecycle hooks
// ------------------------------------------------------------- //
onMounted(async () => {
  if (props.assistanceProduct) {
    rsaStatus.value = getRsaPolicyStatusDetails(props.assistanceProduct);
    rsaContent.value = await roadsideStore.getRsaProductContent(props.assistanceProduct.productCode);
    console.log('RSA Content:', rsaContent.value);
  }

  showAdditionalWarning();
});

// Functions
// ------------------------------------------------------------- //

const primaryBtnLinkClick = () => {
  window.open(
    props.isBank ? props.fields?.primaryBankLink?.value?.href : props.fields?.primaryBtnLink?.value?.href,
    '_blank'
  );
};

const secondaryBtnLinkClick = () => {
  if (props.isBank) {
    if (isIOS()) {
      window.open(props.fields?.appleStoreLink?.value.href, '_blank');
    } else {
      isAndroid()
        ? window.open(props.fields?.andriodStoreLink?.value?.href, '_blank')
        : window.open(props.fields?.secondaryBankLink?.value?.href, '_blank');
    }
  } else if (isIOS()) {
    window.open(props.fields?.lifeStyleAppleAppLink?.value.href, '_blank');
  } else {
    isAndroid()
      ? window.open(props.fields?.lifeStyleAndroidAppLink?.value?.href, '_blank')
      : window.open(props.fields?.secondaryBtnLink?.value?.href, '_blank');
  }
};

const replaceBookmarkContent = (content?: string): string => {
  if (props.assistanceProduct) {
    return replaceBookmark(
      Bookmarks.termEndDate,
      content ?? '',
      formatDate(new Date(props.assistanceProduct.endDate), 'dd/MM/yyyy')
    );
  }
  return '';
};

const showAdditionalWarning = () => {
  if (!rsaStatus.value?.isActive) {
    if (rsaStatus.value?.renewalProducedDirectDebit) {
      const message = rsaStatus.value.isCMO
        ? props.fields?.rsaCmoRenewalProductedMessage?.value
        : props.fields?.rsaRenewalProducedDirectDebitMessage?.value;
      //if RSA product is not active
      warningMessage.value = replaceBookmarkContent(
        rsaStatus.value.isLifeStyle ? props.fields?.lifeStyleRenewalProducedDirectDebitMessage?.value : message
      );
    }
    if (rsaStatus.value?.renewalProducedInvoice) {
      const message = rsaStatus.value.isCMO
        ? props.fields?.rsaCmoRenewalProductedMessage?.value
        : props.fields?.rsaRenewalProducedInvoiceMessage?.value;

      warningMessage.value = replaceBookmarkContent(
        rsaStatus.value.isLifeStyle ? props.fields?.lifeStyleRenewalProducedInvoiceMessage?.value : message
      );
    }
    if (rsaStatus.value?.reminderProducedDirectDebit) {
      const message = rsaStatus.value.isCMO
        ? props.fields?.rsaCmoReminderProducedMessage?.value
        : props.fields?.rsaReminderProducedDirectDebitMessage?.value;

      warningMessage.value = replaceBookmarkContent(
        rsaStatus.value.isLifeStyle ? props.fields?.lifeStyleReminderProducedDirectDebitMessage?.value : message
      );
    }
    if (rsaStatus.value?.reminderProducedInvoice) {
      const message = rsaStatus.value.isCMO
        ? props.fields?.rsaCmoReminderProducedMessage?.value
        : props.fields?.rsaReminderProducedInvoiceMessage?.value;

      warningMessage.value = replaceBookmarkContent(
        rsaStatus.value.isLifeStyle ? props.fields?.lifeStyleReminderProducedInvoiceMessage?.value : message
      );
    }
    if (rsaStatus.value?.isExpiredOrLapse) {
      if (props.assistanceProduct?.status == AssistanceStatus.Lapsed) {
        const message = rsaStatus.value.isCMO
          ? props.fields?.rsaCmoLapsedMessage?.value
          : props.fields?.rsaLapsedMessage?.value;

        warningMessage.value = replaceBookmarkContent(
          rsaStatus.value.isLifeStyle ? props.fields?.lifeStyleLapsedMessage?.value : message
        );
      } else {
        const message = rsaStatus.value.isCMO
          ? props.fields?.rsaCmoExpiredMessage?.value
          : props.fields?.rsaExpiredMessage?.value;
        warningMessage.value = replaceBookmarkContent(
          rsaStatus.value.isLifeStyle ? props.fields?.lifeStyleExpiredMessage?.value : message
        );
      }
    }
    if (rsaStatus.value?.pendingPayment) {
      warningMessage.value = replaceBookmarkContent(
        rsaStatus.value.isLifeStyle
          ? props.fields?.lifeStylePendingPaymentMessage?.value
          : props.fields?.pendingPaymentMessage?.value
      );
    }
  }
};
</script>
